import { useMemo, useState } from "react";

export interface ScoreMiniChartPoint {
  snapshot_date: string;
  alpha_score: number | string | null;
}

interface ScoreMiniChartProps {
  historyData: ScoreMiniChartPoint[];
  width?: number;
  height?: number;
  label?: string;
  showLabel?: boolean;
  interactive?: boolean;
  className?: string;
}

function formatLabelDate(iso: string | null | undefined) {
  if (!iso) return "";

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }

  return `${date.toLocaleString("en-US", { month: "short" })} ${date.getUTCDate()}`;
}

export function ScoreMiniChart({
  historyData,
  width = 300,
  height = 100,
  label = "Score Trajectory",
  showLabel = true,
  interactive = true,
  className = "mini-chart",
}: ScoreMiniChartProps) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const points = useMemo(
    () =>
      historyData.filter(
        (entry): entry is ScoreMiniChartPoint & { alpha_score: number | string } =>
          entry.alpha_score != null && Number.isFinite(Number(entry.alpha_score))
      ),
    [historyData]
  );

  if (points.length < 2) {
    return null;
  }

  const scores = points.map((point) => Number(point.alpha_score));
  const dates = points.map((point) => point.snapshot_date);
  const min = Math.min(...scores) - 3;
  const max = Math.max(...scores) + 3;
  const range = max - min || 1;
  const padding = interactive ? 6 : 3;

  const coords = scores.map((score, index) => ({
    x: padding + (index / (scores.length - 1)) * (width - padding * 2),
    y: height - padding - ((score - min) / range) * (height - padding * 2),
  }));

  const polylinePoints = coords.map((coord) => `${coord.x},${coord.y}`).join(" ");
  const hovered = interactive && hoverIdx != null ? coords[hoverIdx] : null;
  const hoveredScore = interactive && hoverIdx != null ? scores[hoverIdx] : null;
  const hoveredDate = interactive && hoverIdx != null ? dates[hoverIdx] : null;

  return (
    <div className={className}>
      {showLabel && (
        <div className="mini-chart-label">
          {hoverIdx != null
            ? `${formatLabelDate(hoveredDate)} · ${hoveredScore?.toFixed(1)}`
            : label}
        </div>
      )}
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseLeave={interactive ? () => setHoverIdx(null) : undefined}
      >
        <title>{label}</title>
        <polyline
          fill="none"
          stroke="var(--accent-primary)"
          strokeWidth="1.5"
          opacity="0.4"
          points={polylinePoints}
        />
        <polyline
          fill="none"
          stroke="var(--accent-primary)"
          strokeWidth={interactive ? "2" : "1.6"}
          points={polylinePoints}
        />
        {hovered && (
          <>
            <line
              x1={hovered.x}
              y1={padding}
              x2={hovered.x}
              y2={height - padding}
              stroke="var(--muted)"
              strokeWidth="1"
              strokeDasharray="3,3"
              opacity="0.5"
            />
            <circle cx={hovered.x} cy={hovered.y} r="4" fill="var(--accent-primary)" />
          </>
        )}
        {interactive &&
          coords.map((coord, index) => (
            // biome-ignore lint/a11y/noStaticElementInteractions: SVG hitboxes are used only to track hover over chart segments.
            <rect
              key={dates[index]}
              x={index === 0 ? 0 : (coord.x + (coords[index - 1]?.x ?? coord.x)) / 2}
              y={0}
              width={
                index === 0
                  ? (coord.x + (coords[1]?.x ?? coord.x)) / 2
                  : index === coords.length - 1
                    ? width - (coord.x + (coords[index - 1]?.x ?? coord.x)) / 2
                    : ((coords[index + 1]?.x ?? coord.x) - (coords[index - 1]?.x ?? coord.x)) / 2
              }
              height={height}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(index)}
            />
          ))}
      </svg>
    </div>
  );
}
